/**
 * HallowayAstorAI — pure behaviour tree for the Stage 9 final boss.
 * PM Halloway + Lord Astor-Pemberton tag-team on a single HP bar.
 *
 * Three phases:
 *
 *   Halloway (HP > 60%)   — PM Halloway: summons manor-praetorian guards,
 *                            cane_strike (close) + oratory_shout (medium) cycle.
 *   Astor    (25–60% HP)  — Lord Astor-Pemberton: hires whitehall-fixers,
 *                            drone_shot (any range) + briefcase_bomb (medium) cycle.
 *   Enraged  (HP ≤ 25%)   — Both: no summons, cane_fury + drone_burst alternating
 *                            at accelerated pace.
 *
 * Summons are one-time per phase: praetorian on Halloway entry, fixer on Astor entry.
 * Phase 3 has no summons.
 */
import { type EnemyAIInput, ENGAGE_RANGE } from '@/systems/EnemyAI';

// ── Range constants ───────────────────────────────────────────────────────────
export const CANE_RANGE = 90;
export const ORATORY_RANGE = 150;
export const BOMB_RANGE = 130;
export const CANE_FURY_RANGE = 90;

// ── Timing constants ──────────────────────────────────────────────────────────
export const CANE_WINDUP_FRAMES = 16;
export const ORATORY_WINDUP_FRAMES = 24;
export const DRONE_WINDUP_FRAMES = 18;
export const BOMB_WINDUP_FRAMES = 26;
export const CANE_FURY_WINDUP_FRAMES = 10;
export const BURST_WINDUP_FRAMES = 12;
export const HA_BACKSTEP_FRAMES = 22;
export const HA_SUMMON_FRAMES = 30;
export const HA_BACKSTEP_SPEED = 1.3;

// ── Phase thresholds ──────────────────────────────────────────────────────────
/** HP fraction below which Halloway yields to Astor-Pemberton. */
export const ASTOR_THRESHOLD = 0.6;
/** HP fraction below which both become enraged. */
export const ENRAGED_THRESHOLD = 0.25;

const BASE_COOLDOWN = 68;

// ── Types ─────────────────────────────────────────────────────────────────────
export type HallowayAstorPhase = 'halloway' | 'astor' | 'enraged';

export type HallowayAstorAttack =
  | 'cane_strike'
  | 'oratory_shout'
  | 'drone_shot'
  | 'briefcase_bomb'
  | 'cane_fury'
  | 'drone_burst';

export type HallowayAstorNode =
  | 'scan'
  | 'approach'
  | 'cane_windup'
  | 'oratory_windup'
  | 'drone_windup'
  | 'bomb_windup'
  | 'cane_fury_windup'
  | 'burst_windup'
  | 'summoning'
  | 'backstep';

export interface HallowayAstorAIState {
  node: HallowayAstorNode;
  phase: HallowayAstorPhase;
  cooldown: number;
  windup: number;
  backstepFrames: number;
  summonFrames: number;
  attackCycle: number;
  praetorianSummoned: boolean;
  fixerSummoned: boolean;
  /** Which summon to emit when the summoning animation completes. */
  pendingSummon: 'praetorian' | 'fixer' | null;
}

export interface HallowayAstorAIInput extends EnemyAIInput {
  /** Current HP ÷ maxHp (0..1). */
  hpFraction: number;
}

export interface HallowayAstorAIOutput {
  moveX: number;
  moveY: number;
  facing: 1 | -1;
  wantsAttack: boolean;
  attackType: HallowayAstorAttack | null;
  /** True on the single frame the adapter should spawn a manor-praetorian guard. */
  wantsSummonPraetorian: boolean;
  /** True on the single frame the adapter should spawn a whitehall-fixer. */
  wantsSummonFixer: boolean;
  nextState: HallowayAstorAIState;
}

export function initialHallowayAstorState(): HallowayAstorAIState {
  return {
    node: 'scan',
    phase: 'halloway',
    cooldown: 0,
    windup: 0,
    backstepFrames: 0,
    summonFrames: 0,
    attackCycle: 0,
    praetorianSummoned: false,
    fixerSummoned: false,
    pendingSummon: null,
  };
}

/**
 * Resolve phase from HP fraction.
 */
export function phaseFor(hpFraction: number): HallowayAstorPhase {
  if (hpFraction <= ENRAGED_THRESHOLD) return 'enraged';
  if (hpFraction <= ASTOR_THRESHOLD) return 'astor';
  return 'halloway';
}

/**
 * Advance the Halloway & Astor behaviour tree by one frame.
 * Pure function — the Phaser adapter applies movement and spawns
 * hitboxes / summons based on the output.
 */
export function tickHallowayAstorAI(
  input: HallowayAstorAIInput,
  state: HallowayAstorAIState,
): HallowayAstorAIOutput {
  const facing: 1 | -1 = input.targetX >= input.selfX ? 1 : -1;

  if (input.isKO || input.isBeingThrown) {
    return noOp(facing, initialHallowayAstorState());
  }

  // ── Phase resolution ──────────────────────────────────────────────────────
  const newPhase = phaseFor(input.hpFraction);
  const phaseChanged = newPhase !== state.phase;
  const attackCycleBase = phaseChanged ? 0 : state.attackCycle;

  const { praetorianSummoned, fixerSummoned } = state;

  // ── Halloway entry: one-time praetorian summon ────────────────────────────
  if (newPhase === 'halloway' && !praetorianSummoned && state.node !== 'summoning') {
    return noOp(facing, {
      ...state,
      node: 'summoning',
      phase: newPhase,
      praetorianSummoned: true,
      cooldown: 0,
      windup: 0,
      backstepFrames: 0,
      summonFrames: HA_SUMMON_FRAMES,
      attackCycle: 0,
      pendingSummon: 'praetorian',
    });
  }

  // ── Astor entry: one-time fixer summon ────────────────────────────────────
  if (newPhase === 'astor' && !fixerSummoned && state.node !== 'summoning') {
    return noOp(facing, {
      ...state,
      node: 'summoning',
      phase: newPhase,
      fixerSummoned: true,
      cooldown: 0,
      windup: 0,
      backstepFrames: 0,
      summonFrames: HA_SUMMON_FRAMES,
      attackCycle: 0,
      pendingSummon: 'fixer',
    });
  }

  // ── Tick counters ─────────────────────────────────────────────────────────
  const cooldown = Math.max(0, state.cooldown - 1);
  const windup = Math.max(0, state.windup - 1);
  const backstepFrames = Math.max(0, state.backstepFrames - 1);
  const summonFrames = Math.max(0, state.summonFrames - 1);

  // ── Hitstun: freeze animation, keep ticking cooldown ─────────────────────
  if (input.isHitstunned) {
    // If a hit interrupts the summon wind-up, re-arm the dedup flag for whichever
    // summon was pending: the spawn signal only fires at wind-up completion, so
    // without this the praetorian/fixer entry summon is silently never delivered.
    // The entry gate re-fires it on a later clean frame — still exactly once.
    const interrupted = state.node === 'summoning';
    return noOp(facing, {
      ...state,
      phase: newPhase,
      praetorianSummoned:
        interrupted && state.pendingSummon === 'praetorian' ? false : praetorianSummoned,
      fixerSummoned: interrupted && state.pendingSummon === 'fixer' ? false : fixerSummoned,
      node: 'scan',
      cooldown,
      windup: 0,
      backstepFrames: 0,
      summonFrames: 0,
      attackCycle: attackCycleBase,
      pendingSummon: null,
    });
  }

  // ── SUMMONING ─────────────────────────────────────────────────────────────
  if (state.node === 'summoning') {
    if (summonFrames > 0) {
      return noOp(facing, {
        ...state,
        phase: newPhase,
        praetorianSummoned,
        fixerSummoned,
        cooldown,
        summonFrames,
      });
    }
    const emit = state.pendingSummon;
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: false,
      attackType: null,
      wantsSummonPraetorian: emit === 'praetorian',
      wantsSummonFixer: emit === 'fixer',
      nextState: {
        ...state,
        node: 'scan',
        phase: newPhase,
        praetorianSummoned,
        fixerSummoned,
        cooldown: cooldownFor(input.aggressionScale, newPhase),
        summonFrames: 0,
        attackCycle: 0,
        pendingSummon: null,
      },
    };
  }

  // ── BACKSTEP ──────────────────────────────────────────────────────────────
  if (state.node === 'backstep') {
    if (backstepFrames > 0) {
      const { dx, dy, dist } = toTarget(input);
      const away = norm(-dx, -dy, dist);
      return {
        moveX: away.x * HA_BACKSTEP_SPEED,
        moveY: away.y * HA_BACKSTEP_SPEED,
        facing,
        wantsAttack: false,
        attackType: null,
        wantsSummonPraetorian: false,
        wantsSummonFixer: false,
        nextState: {
          ...state,
          phase: newPhase,
          praetorianSummoned,
          fixerSummoned,
          cooldown,
          backstepFrames,
          attackCycle: attackCycleBase,
        },
      };
    }
    return noOp(facing, {
      ...state,
      phase: newPhase,
      praetorianSummoned,
      fixerSummoned,
      cooldown,
      node: 'scan',
      backstepFrames: 0,
      attackCycle: attackCycleBase,
    });
  }

  // ── WINDUP NODES ──────────────────────────────────────────────────────────

  if (state.node === 'cane_windup') {
    if (windup > 0) {
      return noOp(facing, {
        ...state,
        phase: newPhase,
        praetorianSummoned,
        fixerSummoned,
        cooldown,
        windup,
      });
    }
    return attack(facing, 'cane_strike', 'backstep', state, {
      phase: newPhase,
      praetorianSummoned,
      fixerSummoned,
      cooldown: cooldownFor(input.aggressionScale, newPhase),
      backstepFrames: HA_BACKSTEP_FRAMES,
      attackCycle: nextCycle(attackCycleBase, 2),
    });
  }

  if (state.node === 'oratory_windup') {
    if (windup > 0) {
      return noOp(facing, {
        ...state,
        phase: newPhase,
        praetorianSummoned,
        fixerSummoned,
        cooldown,
        windup,
      });
    }
    return attack(facing, 'oratory_shout', 'scan', state, {
      phase: newPhase,
      praetorianSummoned,
      fixerSummoned,
      cooldown: cooldownFor(input.aggressionScale, newPhase),
      attackCycle: nextCycle(attackCycleBase, 2),
    });
  }

  if (state.node === 'drone_windup') {
    if (windup > 0) {
      return noOp(facing, {
        ...state,
        phase: newPhase,
        praetorianSummoned,
        fixerSummoned,
        cooldown,
        windup,
      });
    }
    return attack(facing, 'drone_shot', 'scan', state, {
      phase: newPhase,
      praetorianSummoned,
      fixerSummoned,
      cooldown: cooldownFor(input.aggressionScale, newPhase),
      attackCycle: nextCycle(attackCycleBase, 2),
    });
  }

  if (state.node === 'bomb_windup') {
    if (windup > 0) {
      return noOp(facing, {
        ...state,
        phase: newPhase,
        praetorianSummoned,
        fixerSummoned,
        cooldown,
        windup,
      });
    }
    return attack(facing, 'briefcase_bomb', 'backstep', state, {
      phase: newPhase,
      praetorianSummoned,
      fixerSummoned,
      cooldown: cooldownFor(input.aggressionScale, newPhase),
      backstepFrames: HA_BACKSTEP_FRAMES,
      attackCycle: nextCycle(attackCycleBase, 2),
    });
  }

  if (state.node === 'cane_fury_windup') {
    if (windup > 0) {
      return noOp(facing, {
        ...state,
        phase: newPhase,
        praetorianSummoned,
        fixerSummoned,
        cooldown,
        windup,
      });
    }
    return attack(facing, 'cane_fury', 'scan', state, {
      phase: newPhase,
      praetorianSummoned,
      fixerSummoned,
      cooldown: cooldownFor(input.aggressionScale, newPhase),
      attackCycle: nextCycle(attackCycleBase, 2),
    });
  }

  if (state.node === 'burst_windup') {
    if (windup > 0) {
      return noOp(facing, {
        ...state,
        phase: newPhase,
        praetorianSummoned,
        fixerSummoned,
        cooldown,
        windup,
      });
    }
    return attack(facing, 'drone_burst', 'scan', state, {
      phase: newPhase,
      praetorianSummoned,
      fixerSummoned,
      cooldown: cooldownFor(input.aggressionScale, newPhase),
      attackCycle: nextCycle(attackCycleBase, 2),
    });
  }

  // ── SCAN / APPROACH → select next attack ──────────────────────────────────
  const { dist } = toTarget(input);
  const attackCycle = attackCycleBase;

  if (cooldown === 0 && input.canAttack) {
    // Phase 1 (Halloway): cane_strike close, oratory_shout medium
    if (newPhase === 'halloway') {
      if (attackCycle === 0 && dist <= CANE_RANGE) {
        return noOp(facing, {
          ...state,
          phase: newPhase,
          praetorianSummoned,
          fixerSummoned,
          cooldown,
          node: 'cane_windup',
          windup: CANE_WINDUP_FRAMES,
          attackCycle,
        });
      }
      if (attackCycle === 1 && dist <= ORATORY_RANGE) {
        return noOp(facing, {
          ...state,
          phase: newPhase,
          praetorianSummoned,
          fixerSummoned,
          cooldown,
          node: 'oratory_windup',
          windup: ORATORY_WINDUP_FRAMES,
          attackCycle,
        });
      }
    }

    // Phase 2 (Astor): drone_shot at any range, briefcase_bomb medium
    if (newPhase === 'astor') {
      if (attackCycle === 0) {
        return noOp(facing, {
          ...state,
          phase: newPhase,
          praetorianSummoned,
          fixerSummoned,
          cooldown,
          node: 'drone_windup',
          windup: DRONE_WINDUP_FRAMES,
          attackCycle,
        });
      }
      if (attackCycle === 1 && dist <= BOMB_RANGE) {
        return noOp(facing, {
          ...state,
          phase: newPhase,
          praetorianSummoned,
          fixerSummoned,
          cooldown,
          node: 'bomb_windup',
          windup: BOMB_WINDUP_FRAMES,
          attackCycle,
        });
      }
    }

    // Phase 3 (Enraged): cane_fury close, drone_burst at any range
    if (newPhase === 'enraged') {
      if (attackCycle === 0 && dist <= CANE_FURY_RANGE) {
        return noOp(facing, {
          ...state,
          phase: newPhase,
          praetorianSummoned,
          fixerSummoned,
          cooldown,
          node: 'cane_fury_windup',
          windup: CANE_FURY_WINDUP_FRAMES,
          attackCycle,
        });
      }
      if (attackCycle === 1) {
        return noOp(facing, {
          ...state,
          phase: newPhase,
          praetorianSummoned,
          fixerSummoned,
          cooldown,
          node: 'burst_windup',
          windup: BURST_WINDUP_FRAMES,
          attackCycle,
        });
      }
    }
  }

  // ── Approach / orbit ──────────────────────────────────────────────────────
  const orbitX = input.targetX + input.orbitOffsetX;
  const orbitY = input.targetY + input.orbitOffsetY;
  const odx = orbitX - input.selfX;
  const ody = orbitY - input.selfY;
  const orbitDist = Math.hypot(odx, ody);

  if (orbitDist > 8) {
    const speedMul = 1 + input.aggressionScale * 0.3;
    const move = norm(odx, ody, orbitDist);
    const node: HallowayAstorNode = dist > ENGAGE_RANGE ? 'scan' : 'approach';
    return {
      moveX: move.x * speedMul,
      moveY: move.y * speedMul,
      facing,
      wantsAttack: false,
      attackType: null,
      wantsSummonPraetorian: false,
      wantsSummonFixer: false,
      nextState: {
        ...state,
        phase: newPhase,
        praetorianSummoned,
        fixerSummoned,
        cooldown,
        node,
        windup: 0,
        backstepFrames: 0,
        summonFrames: 0,
        attackCycle,
      },
    };
  }

  return noOp(facing, {
    ...state,
    phase: newPhase,
    praetorianSummoned,
    fixerSummoned,
    cooldown,
    node: 'scan',
    attackCycle,
  });
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function cooldownFor(aggressionScale: number, phase: HallowayAstorPhase): number {
  const base = phase === 'enraged' ? Math.round(BASE_COOLDOWN * 0.55) : BASE_COOLDOWN;
  return Math.round(base * (1 - aggressionScale * 0.5));
}

function toTarget(input: HallowayAstorAIInput): { dx: number; dy: number; dist: number } {
  const dx = input.targetX - input.selfX;
  const dy = input.targetY - input.selfY;
  return { dx, dy, dist: Math.hypot(dx, dy) };
}

function norm(x: number, y: number, len: number): { x: number; y: number } {
  const d = len || 1;
  return { x: x / d, y: y / d };
}

function noOp(facing: 1 | -1, nextState: HallowayAstorAIState): HallowayAstorAIOutput {
  return {
    moveX: 0,
    moveY: 0,
    facing,
    wantsAttack: false,
    attackType: null,
    wantsSummonPraetorian: false,
    wantsSummonFixer: false,
    nextState,
  };
}

/** Emit a single attack and transition to nextNode. */
function attack(
  facing: 1 | -1,
  attackType: HallowayAstorAttack,
  nextNode: HallowayAstorNode,
  state: HallowayAstorAIState,
  overrides: Partial<HallowayAstorAIState>,
): HallowayAstorAIOutput {
  return {
    moveX: 0,
    moveY: 0,
    facing,
    wantsAttack: true,
    attackType,
    wantsSummonPraetorian: false,
    wantsSummonFixer: false,
    nextState: {
      ...state,
      ...overrides,
      node: nextNode,
      windup: 0,
      pendingSummon: null,
    },
  };
}

function nextCycle(c: number, mod: number): number {
  return (c + 1) % mod;
}
